import { JSX, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import { Alert, Button } from '@mui/material';
import { permissionManager } from '../permissions/manager';
import {
    MAINTENANCE_CHANGED,
    MAINTENANCE_PERMISSION,
    MaintenanceBannerContent,
    MaintenanceWindow,
    NO_WINDOW,
    bannerFor,
    loadMaintenance,
} from './maintenanceApi';

/** Once a minute: a window moves through its phases on the minute at best. */
const POLL_MS: number = 60000;

/**
 * A maintenance window, on every screen, for every admin (Stage 5 of Deployment-Resilience-Plan.md).
 *
 * Silent when there is no window. An admin who may change the window is offered a way to it; the
 * rest are only told.
 */
export const MaintenanceBanner = (): JSX.Element | null => {
    const { permissions } = usePermissions();
    const [maintenance, setMaintenance] = useState<MaintenanceWindow>(NO_WINDOW);

    const poll = useCallback(async (): Promise<void> => {
        try {
            setMaintenance(await loadMaintenance());
        } catch {
            // A failed poll keeps what was last known. The next tick tries again.
        }
    }, []);

    useEffect((): (() => void) => {
        void poll();
        const timer: number = window.setInterval((): void => { void poll(); }, POLL_MS);
        const onChanged = (): void => { void poll(); };
        window.addEventListener(MAINTENANCE_CHANGED, onChanged);

        return (): void => {
            window.clearInterval(timer);
            window.removeEventListener(MAINTENANCE_CHANGED, onChanged);
        };
    }, [poll]);

    const content: MaintenanceBannerContent | null = bannerFor(maintenance);
    if (content === null) {
        return null;
    }

    const canManage: boolean = permissionManager.isAllowed(permissions, MAINTENANCE_PERMISSION);

    return (
        <Alert
            severity={ content.severity }
            sx={ { mb: 2 } }
            action={ canManage
                ? <Button component={ RouterLink } to="/maintenance" color="inherit" size="small">{ 'Manage' }</Button>
                : undefined }
        >
            { content.text }
        </Alert>
    );
};
